/* eslint-disable linebreak-style */
import React, {useEffect, useMemo, useState} from 'react';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/styles';
import Loader from 'react-loader-spinner';
import { Card } from '@material-ui/core';

import videoList from './data';
import { selectOrderCurrentProduct } from '../../../../redux/order/order.selectors';
import { selectOtherLoading } from '../../../../redux/other/other.selectors';
import { t } from '../../../../i18n';
import './orderstable.css';

const useStyles = makeStyles(() => ({
  spinner:{
    textAlign: 'center',
    padding:'150px'
  }
}));

const VIMEO_SCRIPT_SRC = 'https://player.vimeo.com/api/player.js';
const DEFAULT_ASPECT_RATIO = '16 / 9';

const buildEmbedUrl = url => {
  if (!url) {
    return '';
  }
  return url.includes('?')
    ? `${url}&badge=0&autopause=0&player_id=0&app_id=58479`
    : `${url}?badge=0&autopause=0&player_id=0&app_id=58479`;
};

const formatAspectRatio = ratio => {
  if (!ratio) {
    return DEFAULT_ASPECT_RATIO;
  }
  const [width, height] = ratio.split(':').map(Number);
  if (!width || !height) {
    return DEFAULT_ASPECT_RATIO;
  }
  return `${width} / ${height}`;
};

const sanitizeText = value => {
  if (typeof value !== 'string') {
    return '';
  }
  return value.trim();
};

const normalizeCode = value => {
  if (value === undefined || value === null) {
    return null;
  }
  return String(value).trim().toLowerCase();
};

const slugify = value => sanitizeText(value)
  .toLowerCase()
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '');

const normalizeCatalog = rawList => rawList.map(group => {
  if (Array.isArray(group.sections)) {
    return {
      id: group.id || slugify(group.name || group.produs || group.codVideo || 'group'),
      name: group.name || group.produs || t('video.groupDefault'),
      codVideo: group.codVideo ?? group.cod ?? null,
      sections: group.sections.map(section => ({
        id: section.id || slugify(section.name || 'general'),
        name: section.name || t('video.sectionDefault'),
        videos: (section.videos || []).map(video => ({
          ...video,
          folder: video.folder || section.name || t('video.sectionDefault'),
          produs: video.produs || group.name || group.produs,
          codVideo: video.codVideo ?? group.codVideo ?? group.cod ?? null
        }))
      }))
    };
  }

  return {
    id: slugify(group.produs || group.codVideo || 'group'),
    name: group.produs || t('video.groupDefault'),
    codVideo: group.codVideo ?? group.cod ?? null,
    sections: [
      {
        id: 'general',
        name: t('video.sectionDefault'),
        videos: (group.filme || []).map((video, index) => ({
          ...video,
          id: video.id || `${slugify(group.produs || 'group')}--general--${index + 1}`,
          folder: t('video.sectionDefault'),
          produs: group.produs,
          codVideo: group.codVideo
        }))
      }
    ]
  };
});

const getProductDisplayName = product => (
  product?.title ||
  product?.denumire ||
  ''
);

const VideoTable = ({currentProduct, loading}) => {
  const classes = useStyles();
  const normalizedVideoCode = useMemo(() => {
    if (!currentProduct) {
      return null;
    }
    const preferredCode = currentProduct.codVideo ?? currentProduct.cod;
    return normalizeCode(preferredCode);
  }, [currentProduct]);
  const normalizedProductName = useMemo(
    () => slugify(getProductDisplayName(currentProduct)),
    [currentProduct]
  );
  const hasProductFilter = Boolean(normalizedVideoCode || normalizedProductName);

  const videoCatalog = useMemo(() => normalizeCatalog(videoList), []);

  const matchingGroupId = useMemo(() => {
    if (normalizedVideoCode) {
      const byCode = videoCatalog.find(
        group => normalizeCode(group.codVideo) === normalizedVideoCode
      );
      if (byCode) {
        return byCode.id;
      }
    }
    if (normalizedProductName) {
      const byName = videoCatalog.find(
        group => slugify(group.name || group.produs || '') === normalizedProductName
      );
      if (byName) {
        return byName.id;
      }
    }
    return null;
  }, [normalizedVideoCode, normalizedProductName, videoCatalog]);

  const availableGroups = useMemo(() => {
    if (!hasProductFilter) {
      return videoCatalog;
    }
    if (!matchingGroupId) {
      return [];
    }
    return videoCatalog.filter(group => group.id === matchingGroupId);
  }, [hasProductFilter, matchingGroupId, videoCatalog]);

  const shouldShowGroupTabs = !hasProductFilter && availableGroups.length > 1;

  const [activeGroupId, setActiveGroupId] = useState(
    matchingGroupId || availableGroups[0]?.id || null
  );
  const [activeSectionId, setActiveSectionId] = useState(null);

  useEffect(() => {
    setActiveGroupId(prev => {
      if (matchingGroupId) {
        return matchingGroupId;
      }
      if (prev && availableGroups.some(group => group.id === prev)) {
        return prev;
      }
      return availableGroups[0]?.id || null;
    });
  }, [matchingGroupId, availableGroups]);

  const activeGroup = useMemo(
    () => availableGroups.find(group => group.id === activeGroupId) || null,
    [availableGroups, activeGroupId]
  );

  const sectionsForGroup = activeGroup?.sections || [];
  const activeSectionName =
    sectionsForGroup.find(section => section.id === activeSectionId)?.name || t('video.allCategories');

  useEffect(() => {
    setActiveSectionId(prev => {
      if (prev && sectionsForGroup.some(section => section.id === prev)) {
        return prev;
      }
      return sectionsForGroup[0]?.id || null;
    });
  }, [sectionsForGroup]);

  const videosForCurrentProduct = useMemo(() => {
    if (!activeGroup) {
      return [];
    }
    const selectedSection =
      sectionsForGroup.find(section => section.id === activeSectionId) || null;
    if (selectedSection) {
      return selectedSection.videos || [];
    }
    return sectionsForGroup.flatMap(section => section.videos || []);
  }, [activeGroup, activeSectionId, sectionsForGroup]);

  const [selectedVideoId, setSelectedVideoId] = useState(
    videosForCurrentProduct[0]?.id ?? null
  );
  const [searchTerm, setSearchTerm] = useState('');

  const filteredVideos = useMemo(() => {
    if (!searchTerm.trim()) {
      return videosForCurrentProduct;
    }
    const term = searchTerm.trim().toLowerCase();
    return videosForCurrentProduct.filter(video => {
      const title = sanitizeText(video.titlu).toLowerCase();
      const description = sanitizeText(video.descriere).toLowerCase();
      return title.includes(term) || description.includes(term);
    });
  }, [videosForCurrentProduct, searchTerm]);

  useEffect(() => {
    if (!filteredVideos.length) {
      setSelectedVideoId(null);
      return;
    }
    setSelectedVideoId(prev => {
      if (prev && filteredVideos.some(video => video.id === prev)) {
        return prev;
      }
      return filteredVideos[0].id;
    });
  }, [filteredVideos]);

  const selectedVideo =
    videosForCurrentProduct.find(video => video.id === selectedVideoId) || null;
  const [modalVideo, setModalVideo] = useState(null);

  const productHasDedicatedVideos = videosForCurrentProduct.length > 0;

  const currentProductLabel =
    getProductDisplayName(currentProduct) ||
    (currentProduct?.cod ? String(currentProduct.cod).toUpperCase() : null);

  const headerTitle = normalizedVideoCode && currentProductLabel
    ? t('video.clipForProduct', { product: currentProductLabel })
    : t('video.demoClips', { suffix: activeGroup?.name ? ` - ${activeGroup.name}` : '' });
  const headerSubtitle = productHasDedicatedVideos
    ? t('video.selectFromList')
    : t('video.noVideosForSelection');

  useEffect(() => {
    if (typeof document === 'undefined') {
      return undefined;
    }
    const existingScript = document.querySelector(`script[src="${VIMEO_SCRIPT_SRC}"]`);
    if (existingScript) {
      return undefined;
    }
    const script = document.createElement('script');
    script.src = VIMEO_SCRIPT_SRC;
    script.async = true;
    document.body.appendChild(script);
    return undefined;
  }, []);

  if (loading) {
    return (
      <div className={classes.spinner}>
        <Loader
          color="#00BFFF"
          height={100}
          type="Puff"
          width={100}
        />
      </div>
    );
  }

  const closeModal = () => {
    setModalVideo(null);
  };

  const renderPlayerPanel = () => (
    <section className="video-player-panel">
      {selectedVideo ? (
        <div className="video-player-card">
          <div
            className="video-player-aspect"
            style={{aspectRatio: formatAspectRatio(selectedVideo.ratio)}}
          >
            <iframe
              src={buildEmbedUrl(selectedVideo.url)}
              frameBorder="0"
              allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              title={sanitizeText(selectedVideo.titlu) || t('video.clipVideo')}
              allowFullScreen
            />
          </div>
          <div className="video-player-meta">
            <h3>{sanitizeText(selectedVideo.titlu) || t('video.clipVideo')}</h3>
            {sanitizeText(selectedVideo.descriere) ? (
              <p>{sanitizeText(selectedVideo.descriere)}</p>
            ) : null}
          </div>
        </div>
      ) : (
        <div className="video-empty-state">
          {t('video.emptySelectPrompt')}
        </div>
      )}
    </section>
  );

  const emptyStateMessage = searchTerm.trim()
    ? t('video.emptySearch')
    : t('video.emptyConfigured');

  const handleSearchChange = event => {
    setSearchTerm(event.target.value);
  };

  const handleDesktopSelection = video => {
    setSelectedVideoId(video.id);
    setModalVideo(null);
  };

  const handleGroupChange = groupId => {
    setActiveGroupId(groupId);
    setModalVideo(null);
  };

  const handleSectionChange = sectionId => {
    setActiveSectionId(sectionId);
    setModalVideo(null);
  };

  const handleMobileSelection = video => {
    setSelectedVideoId(video.id);
    if (
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(max-width: 640px)').matches
    ) {
      setModalVideo(video);
    } else {
      setModalVideo(null);
    }
  };

  return (
    <Card className="control-panel">
      <div className="control-section grid-container" >
        <div className="video-page-wrapper">
          <div className="video-layout">
            <aside className="video-list-panel">
              <div className="video-list-panel-header">
                <span className="video-list-counter">
                  {activeSectionName}
                </span>
                {activeGroup?.name && (
                  <span className="video-list-product">
                    {activeGroup.name}
                  </span>
                )}
              </div>
              {shouldShowGroupTabs ? (
                <div className="video-group-tabs">
                  {availableGroups.map(group => (
                    <button
                      key={group.id}
                      type="button"
                      className={`video-group-tab ${group.id === activeGroupId ? 'active' : ''}`}
                      onClick={() => handleGroupChange(group.id)}
                    >
                      {group.name}
                    </button>
                  ))}
                </div>
              ) : null}
              <div className="video-search-row">
                <label htmlFor="video-search-input">{t('video.searchLabel')}</label>
                <input
                  id="video-search-input"
                  className="video-search-input"
                  type="text"
                  placeholder={t('video.searchPlaceholder')}
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
              </div>
              {filteredVideos.length ? (
                <div className="video-board">
                  <div className="video-board-head">
                    <span>{t('video.boardCategories')}</span>
                    <span>{t('video.boardVideo')}</span>
                  </div>
                  <div className="video-board-body">
                    <div className="video-board-sections">
                      {sectionsForGroup.map(section => (
                        <button
                          key={section.id}
                          type="button"
                          className={`video-board-section-item ${section.id === activeSectionId ? 'active' : ''}`}
                          onClick={() => handleSectionChange(section.id)}
                        >
                          <span className="video-folder-icon" aria-hidden="true">📁</span>
                          <span>{section.name}</span>
                        </button>
                      ))}
                    </div>
                    <div className="video-board-videos">
                      {filteredVideos.map(video => {
                        const description = sanitizeText(video.descriere);
                        return (
                          <button
                            type="button"
                            key={`${video.produs || 'global'}-${video.id}`}
                            className={`video-board-video-item ${
                              selectedVideoId === video.id ? 'active' : ''
                            }`}
                            onClick={() => handleDesktopSelection(video)}
                          >
                            <div className="video-item-main">
                              <span className="video-item-icon-camera" aria-hidden="true">🎥</span>
                              <div className="video-list-item-title">
                                {sanitizeText(video.titlu) || t('video.clipVideo')}
                              </div>
                            </div>
                            {description ? (
                              <p className="video-list-item-description">{description}</p>
                            ) : null}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="video-empty-state">{emptyStateMessage}</div>
              )}
            </aside>
            {renderPlayerPanel()}
          </div>
          <header className="video-page-header mobile-only">
            {/* <p className="video-page-eyebrow">Biblioteca video</p> */}
            <h2>{headerTitle}</h2>
            <p className="video-page-subtitle">{headerSubtitle}</p>
          </header>
          <div className="video-mobile-filters">
            {shouldShowGroupTabs ? (
              <div className="video-group-tabs">
                {availableGroups.map(group => (
                  <button
                    key={`mobile-group-${group.id}`}
                    type="button"
                    className={`video-group-tab ${group.id === activeGroupId ? 'active' : ''}`}
                    onClick={() => handleGroupChange(group.id)}
                  >
                    {group.name}
                  </button>
                ))}
              </div>
            ) : null}
            {sectionsForGroup.length > 1 ? (
              <div className="video-section-chips">
                {sectionsForGroup.map(section => (
                  <button
                    key={`mobile-section-${section.id}`}
                    type="button"
                    className={`video-section-chip ${section.id === activeSectionId ? 'active' : ''}`}
                    onClick={() => handleSectionChange(section.id)}
                  >
                    {section.name}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
          <div className="video-mobile-search">
            <label htmlFor="video-mobile-search-input">{t('video.mobileSearchLabel')}</label>
            <input
              id="video-mobile-search-input"
              className="video-search-input"
              type="text"
              placeholder={t('video.mobileSearchPlaceholder')}
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </div>
          <div className="video-mobile-list">
            {filteredVideos.length ? (
              filteredVideos.map(video => {
                const description = sanitizeText(video.descriere);
                return (
                  <button
                    type="button"
                    key={`mobile-${video.produs || 'global'}-${video.id}`}
                    className={`video-mobile-card ${
                      selectedVideoId === video.id ? 'active' : ''
                    }`}
                    onClick={() => handleMobileSelection(video)}
                  >
                    <div className="video-mobile-thumb">
                      {video.thumbnail ? (
                        <img
                          src={video.thumbnail}
                          alt={sanitizeText(video.titlu) || t('video.thumbnailAlt')}
                        />
                      ) : (
                        <div className="video-thumb-placeholder">
                          {(video.produs || 'ALL').toUpperCase()}
                        </div>
                      )}
                      <span className="video-mobile-icon">▶</span>
                    </div>
                    <div className="video-mobile-meta">
                      <h4>{sanitizeText(video.titlu) || t('video.clipVideo')}</h4>
                      {description ? <p>{description}</p> : null}
                    </div>
                  </button>
                );
              })
            ) : (
              <div className="video-empty-state">{emptyStateMessage}</div>
            )}
          </div>
          {modalVideo && (
            <div
              className="video-modal-backdrop"
              role="dialog"
              aria-modal="true"
              aria-label={sanitizeText(modalVideo.titlu) || t('video.clipVideo')}
              onClick={closeModal}
            >
              <div
                className="video-modal-content"
                onClick={event => event.stopPropagation()}
              >
                <button
                  type="button"
                  className="video-modal-close"
                  onClick={closeModal}
                  aria-label={t('video.closePlayer')}
                >
                  ×
                </button>
                <div className="video-modal-player">
                  <iframe
                    src={buildEmbedUrl(modalVideo.url)}
                    frameBorder="0"
                    allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    title={sanitizeText(modalVideo.titlu) || t('video.clipVideo')}
                    allowFullScreen
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};

VideoTable.propTypes = {
  currentProduct:PropTypes.object,
  loading:PropTypes.bool
};

const mapStateToProps=state=>(
  {
  currentProduct:selectOrderCurrentProduct(state),
  loading:selectOtherLoading(state),
  }
);

export default connect(mapStateToProps)(VideoTable);
